// Paste reset and clear-only flags on ordinary keys adapted from pingdotgg/t3code (MIT),
// apps/web/src/shortcutModifierState.ts at 7202ba666.
import { useEffect, useRef, useState } from "react";

export interface ShortcutModifierState {
  metaKey: boolean;
  ctrlKey: boolean;
  altKey: boolean;
  shiftKey: boolean;
}

const EMPTY_SHORTCUT_MODIFIER_STATE: ShortcutModifierState = {
  metaKey: false,
  ctrlKey: false,
  altKey: false,
  shiftKey: false,
};

export function areShortcutModifierStatesEqual(
  left: ShortcutModifierState,
  right: ShortcutModifierState,
): boolean {
  return (
    left.metaKey === right.metaKey &&
    left.ctrlKey === right.ctrlKey &&
    left.altKey === right.altKey &&
    left.shiftKey === right.shiftKey
  );
}

/**
 * How long a modifier held across a paste may stay marked held without a
 * keyup. Dictation's synthetic ⌘V never sends one; a person holding ⌘ after
 * pasting does, usually well within this window.
 */
export const PASTE_MODIFIER_RESET_DELAY_MS = 1000;

const MODIFIER_KEYS = ["metaKey", "ctrlKey", "altKey", "shiftKey"] as const;

export function useShortcutModifierState(): ShortcutModifierState {
  const [state, setState] = useState(EMPTY_SHORTCUT_MODIFIER_STATE);
  const stateRef = useRef(EMPTY_SHORTCUT_MODIFIER_STATE);

  useEffect(() => {
    // Server rendering and Node-run component tests have no window to watch.
    if (typeof window === "undefined") return;
    const updateState = (next: ShortcutModifierState) => {
      // Even a no-op state dispatch can cost work in the sidebar's large tree.
      // Ordinary typing must return before dispatching a React update.
      if (areShortcutModifierStatesEqual(stateRef.current, next)) return;
      stateRef.current = next;
      setState(next);
    };
    // Each modifier's latest keydown, numbered in arrival order, so a paste
    // can tell a modifier pressed again after it from one left over.
    let keydownSequence = 0;
    const lastKeydown: Record<keyof ShortcutModifierState, number> = {
      metaKey: 0,
      ctrlKey: 0,
      altKey: 0,
      shiftKey: 0,
    };
    let pasteResetTimer: ReturnType<typeof setTimeout> | null = null;
    const cancelPasteReset = () => {
      if (pasteResetTimer === null) return;
      clearTimeout(pasteResetTimer);
      pasteResetTimer = null;
    };

    const onKeyboardEvent = (event: KeyboardEvent) => {
      const modifier = normalizeModifierKey(event.key);
      if (modifier && event.type === "keydown") lastKeydown[modifier] = ++keydownSequence;
      updateState(shortcutModifierStateAfterKeyboardEvent(stateRef.current, event));
    };
    // Dictation tools (Wispr Flow) paste with a synthetic ⌘V whose Meta keyup
    // never reaches the page, so the tracked state would stay "⌘ held" — and
    // the composer's send button would stay on its alternate action. An
    // ordinary ⌘V looks the same until ⌘ comes up, and ⌘ may still be held on
    // purpose. So a paste does not clear modifiers at once: one still marked
    // held a moment later, with no fresh keydown since the paste, is taken to
    // be the dictation leftover and cleared. A physically held modifier
    // re-registers on its next keydown.
    const onPaste = () => {
      const held = MODIFIER_KEYS.filter((key) => stateRef.current[key]);
      if (held.length === 0) return;
      const pasteSequence = keydownSequence;
      cancelPasteReset();
      pasteResetTimer = setTimeout(() => {
        pasteResetTimer = null;
        const next = { ...stateRef.current };
        for (const key of held) {
          if (lastKeydown[key] <= pasteSequence) next[key] = false;
        }
        updateState(next);
      }, PASTE_MODIFIER_RESET_DELAY_MS);
    };
    const onBlur = () => {
      cancelPasteReset();
      updateState(EMPTY_SHORTCUT_MODIFIER_STATE);
    };

    window.addEventListener("keydown", onKeyboardEvent, true);
    window.addEventListener("keyup", onKeyboardEvent, true);
    window.addEventListener("paste", onPaste, true);
    window.addEventListener("blur", onBlur);
    return () => {
      cancelPasteReset();
      window.removeEventListener("keydown", onKeyboardEvent, true);
      window.removeEventListener("keyup", onKeyboardEvent, true);
      window.removeEventListener("paste", onPaste, true);
      window.removeEventListener("blur", onBlur);
    };
  }, []);

  return state;
}

function normalizeModifierKey(key: string): keyof ShortcutModifierState | null {
  switch (key) {
    case "Meta":
    case "OS":
    case "Command":
      return "metaKey";
    case "Control":
      return "ctrlKey";
    case "Alt":
    case "Option":
      return "altKey";
    case "Shift":
      return "shiftKey";
    default:
      return null;
  }
}

export function shortcutModifierStateAfterKeyboardEvent(
  currentState: ShortcutModifierState,
  event: KeyboardEvent,
): ShortcutModifierState {
  const normalizedModifierKey = normalizeModifierKey(event.key);
  let nextState: ShortcutModifierState;
  if (normalizedModifierKey) {
    nextState = {
      ...currentState,
      [normalizedModifierKey]: event.type === "keydown",
    };
  } else {
    // Flags on non-modifier keys may only clear a bit, never set one. After a
    // dictation tool's synthetic ⌘V (Wispr Flow), the browser can keep
    // reporting metaKey=true on real key events (Enter to submit) until the
    // user physically taps ⌘. Trusting that flag would mark ⌘ as held. Setting
    // a bit requires a real modifier keydown, handled above.
    nextState = {
      metaKey: currentState.metaKey && event.metaKey,
      ctrlKey: currentState.ctrlKey && event.ctrlKey,
      altKey: currentState.altKey && event.altKey,
      shiftKey: currentState.shiftKey && event.shiftKey,
    };
  }

  return areShortcutModifierStatesEqual(currentState, nextState) ? currentState : nextState;
}
